// ritual.js — trận pháp: the little rite a caster traces before a skill lands. The
// player taps a chain of nodes in order; how FAST they finish sets a speed multiplier,
// and how cleanly they finish sets a combo bonus on top of it.
//
// Pure: no DOM, no clock, no randomness of its own. The UI feeds it outcomes and an
// elapsed time, and it answers with a number the rules multiply damage by. That split
// is the point — the mini-game is skill expression, the rules stay deterministic.

export const NODE_IDS = ['A', 'B', 'C', 'D', 'E', 'F']

// Whole-chain time -> power. Read as "finish inside 3s for 150%". The last band has no
// upper bound; anything slower than the one before it lands there.
export const DEFAULT_TIERS = [
  { underMs: 3000, power: 1.5 },
  { underMs: 6000, power: 1.0 },
  { underMs: 12000, power: 0.7 },
  { underMs: Infinity, power: 0.5 },
]

export const DEFAULT_COMBO_STEP = 0.05 // +5% per correct hit still standing at the end
export const MAX_STEPS = 16

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n))

export function normalizeTiers(raw) {
  if (!Array.isArray(raw) || !raw.length) return DEFAULT_TIERS
  const tiers = raw
    .map(t => ({
      underMs: Number.isFinite(t?.underMs) && t.underMs > 0 ? t.underMs : Infinity,
      power: Number.isFinite(t?.power) ? clamp(t.power, 0, 10) : 1,
    }))
    .sort((a, b) => a.underMs - b.underMs)
  // The slowest band must catch everything, or a slow cast would score nothing.
  if (tiers[tiers.length - 1].underMs !== Infinity) tiers.push({ underMs: Infinity, power: tiers[tiers.length - 1].power })
  return tiers
}

/**
 * The chain to trace. Nodes may repeat (A -> B -> C -> A -> D -> F) but never twice in
 * a row, because tapping the same node twice reads as one press the player already made.
 */
export function makeChain(steps, { rnd = Math.random, nodes = NODE_IDS } = {}) {
  const pool = Array.isArray(nodes) && nodes.length >= 2 ? nodes : NODE_IDS
  const length = clamp(Math.round(Number(steps) || 0), 1, MAX_STEPS)
  const chain = []
  for (let i = 0; i < length; i++) {
    let pick = pool[Math.floor(clamp(rnd(), 0, 0.999999) * pool.length)]
    // One retry is enough to break a repeat without looping on a degenerate rnd.
    if (chain.length && pick === chain[chain.length - 1]) {
      const next = pool[(pool.indexOf(pick) + 1) % pool.length]
      pick = next
    }
    chain.push(pick)
  }
  return chain
}

export function tierFor(elapsedMs, tiers = DEFAULT_TIERS) {
  const ms = Number.isFinite(elapsedMs) && elapsedMs >= 0 ? elapsedMs : Infinity
  for (const tier of tiers) if (ms < tier.underMs) return tier
  return tiers[tiers.length - 1]
}

/**
 * The combo the player FINISHES on, not the best they ever held. A miss on the last
 * node wipes the bonus even after five clean hits — that is the tension the rule is
 * for, and it is exactly what the worked examples describe.
 */
export function trailingStreak(outcomes = []) {
  let streak = 0
  for (let i = outcomes.length - 1; i >= 0; i--) {
    if (outcomes[i] === true || outcomes[i] === 'hit') streak++
    else break
  }
  return streak
}

/**
 * @param {{outcomes: Array, elapsedMs: number, tiers?: Array, comboStep?: number}} input
 * @returns {{power: number, speed: number, combo: number, streak: number, hits: number, total: number}}
 *   power = speed x (1 + combo). An empty or abandoned chain scores nothing (power 0),
 *   which the caller reads as "do not cast".
 */
export function scoreRitual({ outcomes = [], elapsedMs = Infinity, tiers, comboStep = DEFAULT_COMBO_STEP } = {}) {
  const bands = normalizeTiers(tiers)
  const total = outcomes.length
  if (!total) return { power: 0, speed: 0, combo: 0, streak: 0, hits: 0, total: 0 }
  const streak = trailingStreak(outcomes)
  const step = Number.isFinite(comboStep) ? clamp(comboStep, 0, 1) : DEFAULT_COMBO_STEP
  const combo = streak * step
  const speed = tierFor(elapsedMs, bands).power
  const hits = outcomes.filter(o => o === true || o === 'hit').length
  // Rounded only enough to kill float noise: the spec quotes 172.5%, so whole percent
  // would be too coarse. Four places keeps the HUD, the log line and the damage agreeing.
  return { power: Math.round(speed * (1 + combo) * 10000) / 10000, speed, combo, streak, hits, total }
}

/**
 * The authored config on a skill, repaired rather than rejected. `steps: 0` or a missing
 * block means the skill casts straight away with no rite, which is the default.
 */
export function normalizeRitual(raw) {
  if (!raw || typeof raw !== 'object') return null
  const steps = clamp(Math.round(Number(raw.steps) || 0), 0, MAX_STEPS)
  if (!steps) return null
  const nodes = clamp(Math.round(Number(raw.nodes) || NODE_IDS.length), 2, NODE_IDS.length)
  return {
    steps,
    nodes,
    tiers: normalizeTiers(raw.tiers),
    comboStep: Number.isFinite(raw.comboStep) ? clamp(raw.comboStep, 0, 1) : DEFAULT_COMBO_STEP,
    ...(typeof raw.title === 'string' && raw.title.trim() ? { title: raw.title.trim().slice(0, 60) } : {}),
  }
}
